// Security cleanup utility for removing sensitive data from localStorage/sessionStorage
// This addresses the localStorage security vulnerability identified in production testing

import { createComponentLogger } from './logger';

const log = createComponentLogger('SecurityCleanup', 'security');

const RETIRED_RATE_QUOTE_STORAGE_KEYS = new Set([
  'lendwise_rate_quotes',
  'rate-quotes-list',
  'lendwise-quotes-migrated',
  'lendwise_closing_cost_templates',
  'lendwise_global_closing_costs',
  'rate_quote_draft',
  'editquote',
  'apr-bug-reports',
  'closing-cost-templates'
]);

const isRetiredRateQuoteStorageKey = (key: string): boolean => {
  const normalized = key.toLowerCase();
  return RETIRED_RATE_QUOTE_STORAGE_KEYS.has(normalized)
    || normalized.startsWith('rate-quote-')
    || normalized.startsWith('feature_flag_rate_quote_');
};

const cleanupRetiredRateQuotePreferences = (storage: Storage): boolean => {
  const key = 'lendwise_user_preferences';
  const serialized = storage.getItem(key);
  if (!serialized) {
    return false;
  }

  try {
    const parsed = JSON.parse(serialized) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return false;
    }

    const preferences = parsed as Record<string, unknown>;
    let changed = false;
    for (const [userId, value] of Object.entries(preferences)) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        continue;
      }
      const userPreferences = value as Record<string, unknown>;
      if (!Object.prototype.hasOwnProperty.call(userPreferences, 'rateQuote')) {
        continue;
      }

      delete userPreferences.rateQuote;
      changed = true;
      if (Object.keys(userPreferences).length === 0) {
        delete preferences[userId];
      }
    }

    if (!changed) {
      return false;
    }
    if (Object.keys(preferences).length === 0) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, JSON.stringify(preferences));
    }
    return true;
  } catch {
    // Preserve malformed shared preference data rather than deleting unrelated values.
    return false;
  }
};

/**
 * Removes browser-only data left by the retired Tools > Rate Quote feature.
 * Artemis Quote Rate does not use these keys.
 */
export const cleanupRetiredRateQuoteStorage = (): number => {
  let removed = 0;

  for (const storage of [localStorage, sessionStorage]) {
    const keysToRemove: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && isRetiredRateQuoteStorageKey(key)) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      storage.removeItem(key);
      removed += 1;
    }

    if (cleanupRetiredRateQuotePreferences(storage)) {
      removed += 1;
    }
  }

  if (removed > 0) {
    log.info(`Removed ${removed} retired Rate Quote browser storage item(s)`);
  }
  return removed;
};

/**
 * Removes sensitive authentication and session data from browser storage
 */
export const cleanupSensitiveData = () => {
  log.debug('Starting sensitive data cleanup');
  
  // Define patterns for sensitive data keys
  const sensitivePatterns = [
    // Supabase auth tokens
    'supabase.auth.token',
    'sb-auth-token',
    'sb-',
    
    // Custom session tokens
    'lendwise-session-token',
    'lendwise-auth',
    
    // Generic auth patterns
    'access_token',
    'refresh_token',
    'bearer_token',
    'auth_token',
    'session_token',
    'jwt',
    
    // API keys and secrets
    'api_key',
    'secret',
    'private_key',
    
    // User credentials
    'password',
    'pin',
    'credential'
  ];
  
  // Clean localStorage
  const localStorageKeysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && isSensitiveKey(key, sensitivePatterns)) {
      localStorageKeysToRemove.push(key);
    }
  }
  
  localStorageKeysToRemove.forEach(key => {
    localStorage.removeItem(key);
    log.info('Removed sensitive localStorage key', { key });
  });
  
  // Clean sessionStorage
  const sessionStorageKeysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && isSensitiveKey(key, sensitivePatterns)) {
      sessionStorageKeysToRemove.push(key);
    }
  }
  
  sessionStorageKeysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
    log.info('Removed sensitive sessionStorage key', { key });
  });
  
  log.info('Sensitive data cleanup complete', { 
    localStorageKeysRemoved: localStorageKeysToRemove.length,
    sessionStorageKeysRemoved: sessionStorageKeysToRemove.length,
    totalRemoved: localStorageKeysToRemove.length + sessionStorageKeysToRemove.length
  });
};

/**
 * Checks if a storage key contains sensitive data based on patterns
 */
const isSensitiveKey = (key: string, patterns: string[]): boolean => {
  const lowerKey = key.toLowerCase();
  
  // Whitelist of keys that are safe and should not be flagged as sensitive
  const whitelist = [
    'supabase_session',     // Legitimate session storage
    'auth-redirect-path',   // Navigation state
    'auth-version',         // Migration tracking
    'lendwise_',           // Business data
    'user-preferences',    // User settings
    'theme'              // UI preferences
  ];
  
  // Skip if whitelisted
  if (whitelist.some(safe => lowerKey.includes(safe))) {
    return false;
  }
  
  return patterns.some(pattern => lowerKey.includes(pattern.toLowerCase()));
};

/**
 * Safe localStorage cleanup that preserves business data
 * Only removes items matching sensitive patterns while keeping legitimate app data
 */
export const safeCleanupSensitiveData = () => {
  log.info('Starting safe sensitive data cleanup');
  
  // Keys to preserve (business data that should NOT be removed)
  const preservePatterns = [
    'lendwise_',           // Business data from localStorage.ts
    'auth-version',        // Auth cleanup version
    'lendwise-dev-',       // Development settings
    'user-preferences',    // User settings
    'fee-',               // Fee management data
    'template-',          // Template data
    'contact-',           // Contact data
    'pipeline-'           // Pipeline data
  ];
  
  // Sensitive patterns that should be removed
  const sensitivePatterns = [
    'supabase.auth.token',
    'sb-auth-token',
    'lendwise-session-token',
    'lendwise-auth-token',
    'access_token',
    'refresh_token',
    'bearer_token',
    'jwt_token',
    'session_id',
    'api_key',
    'secret_key'
  ];
  
  // Check localStorage
  const localStorageKeysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const shouldPreserve = preservePatterns.some(pattern => 
        key.toLowerCase().includes(pattern.toLowerCase())
      );
      
      const isSensitive = sensitivePatterns.some(pattern => 
        key.toLowerCase().includes(pattern.toLowerCase())
      );
      
      if (isSensitive && !shouldPreserve) {
        localStorageKeysToRemove.push(key);
      }
    }
  }
  
  localStorageKeysToRemove.forEach(key => {
    localStorage.removeItem(key);
    log.info('Safely removed sensitive localStorage key', { key });
  });
  
  // Check sessionStorage (can be more aggressive since it's temporary)
  const sessionStorageKeysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key) {
      // Keep only specific non-sensitive items in sessionStorage
      const keepInSession = [
        'auth-redirect-path',  // Redirect after auth
        'lendwise-dev-mode'    // Development settings
      ];
      
      const shouldKeep = keepInSession.some(pattern => 
        key.toLowerCase().includes(pattern.toLowerCase())
      );
      
      if (!shouldKeep) {
        sessionStorageKeysToRemove.push(key);
      }
    }
  }
  
  sessionStorageKeysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
    log.info('Safely removed sensitive sessionStorage key', { key });
  });
  
  log.info('Safe sensitive data cleanup complete', {
    localStorageKeysRemoved: localStorageKeysToRemove.length,
    sessionStorageKeysRemoved: sessionStorageKeysToRemove.length,
    totalRemoved: localStorageKeysToRemove.length + sessionStorageKeysToRemove.length
  });
};

/**
 * Audit current browser storage for sensitive data
 * Returns a report of potentially sensitive items found
 */
export const auditBrowserStorage = (): {
  localStorage: string[];
  sessionStorage: string[];
  summary: {
    totalSensitiveItems: number;
    localStorageSensitive: number;
    sessionStorageSensitive: number;
  };
} => {
  const sensitivePatterns = [
    'token', 'auth', 'session', 'jwt', 'bearer', 'secret', 'key', 'credential', 'password'
  ];
  
  const localStorageSensitive: string[] = [];
  const sessionStorageSensitive: string[] = [];
  
  // Check localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && isSensitiveKey(key, sensitivePatterns)) {
      localStorageSensitive.push(key);
    }
  }
  
  // Check sessionStorage
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && isSensitiveKey(key, sensitivePatterns)) {
      sessionStorageSensitive.push(key);
    }
  }
  
  return {
    localStorage: localStorageSensitive,
    sessionStorage: sessionStorageSensitive,
    summary: {
      totalSensitiveItems: localStorageSensitive.length + sessionStorageSensitive.length,
      localStorageSensitive: localStorageSensitive.length,
      sessionStorageSensitive: sessionStorageSensitive.length
    }
  };
};

/**
 * Initialize security cleanup on app startup
 * This should be called early in the app lifecycle
 */
export const initializeSecurityCleanup = () => {
  // Perform audit first
  const audit = auditBrowserStorage();
  
  if (audit.summary.totalSensitiveItems > 0) {
    log.warn('Found potentially sensitive items in browser storage', audit);
    
    // Perform safe cleanup
    safeCleanupSensitiveData();
    
    // Audit again to confirm cleanup
    const postCleanupAudit = auditBrowserStorage();
    log.info('Post-cleanup audit results', postCleanupAudit);
  } else {
    log.info('No sensitive items found in browser storage');
  }
};
